import type { Dispatch, SetStateAction } from "react";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import OrganizerDatePicker from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatDateTimeLabel } from "../../../shared/utils/dateTime";
import type {
  AdminTournamentBracketMatch,
  AdminTournamentScheduleSlot,
  ParticipantCheckin,
} from "../services/eventRegistrationAdminService";
import {
  categoryLabel,
  dateOnlyFromLocal,
  formatSets,
  inProgressElapsedLabel,
  matchStatusLabel,
  roundLabel,
  slotCourtSelection,
  slotMatchId,
  timeOnlyFromLocal,
  timelineStatusClass,
  timelineStatusValue,
  toDateTimeLocal,
  type TournamentOperationCategory,
} from "../utils/tournamentOperations";
import AdminMatchCheckInControl, {
  type MatchCheckInSide,
} from "./AdminMatchCheckInControl";

export type ScheduleEditDraft = Record<
  string,
  {
    date: string;
    startTime: string;
    durationMinutes: string;
    courtName: string;
  }
>;

type AdminTournamentTimelinePanelProps = {
  bracketMatchById: Map<number, AdminTournamentBracketMatch>;
  categories: TournamentOperationCategory[];
  categoryKey: string;
  courtNames: string[];
  dayOptions: string[];
  loadingSchedule: boolean;
  nowMs: number;
  onOpenScoreModal: (
    match: AdminTournamentBracketMatch,
    categoryKey: string,
  ) => void;
  onStartSlotMatch: (
    slot: AdminTournamentScheduleSlot,
    match: AdminTournamentBracketMatch,
    courtNameOverride?: string,
  ) => void;
  onSaveSlotSchedule: (
    slot: AdminTournamentScheduleSlot,
    edit: ScheduleEditDraft[string],
  ) => void;
  onToggleCheckInSide: (side: MatchCheckInSide, checkedIn: boolean) => void;
  participantCheckins: ParticipantCheckin[];
  pendingCheckInSideId?: string | null;
  saving: boolean;
  scheduleEdits: ScheduleEditDraft;
  setScheduleEdits: Dispatch<SetStateAction<ScheduleEditDraft>>;
  setTimelineCategoryFilter: Dispatch<SetStateAction<string>>;
  setTimelineDayFilter: Dispatch<SetStateAction<string>>;
  setTimelineStatusFilter: Dispatch<SetStateAction<string>>;
  timelineCategoryFilter: string;
  timelineDayFilter: string;
  timelineStatusFilter: string;
  tournamentStarted: boolean;
  visibleTimelineSlots: AdminTournamentScheduleSlot[];
};

export default function AdminTournamentTimelinePanel({
  bracketMatchById,
  categories,
  categoryKey,
  courtNames,
  dayOptions,
  loadingSchedule,
  nowMs,
  onOpenScoreModal,
  onSaveSlotSchedule,
  onStartSlotMatch,
  onToggleCheckInSide,
  participantCheckins,
  pendingCheckInSideId = null,
  saving,
  scheduleEdits,
  setScheduleEdits,
  setTimelineCategoryFilter,
  setTimelineDayFilter,
  setTimelineStatusFilter,
  timelineCategoryFilter,
  timelineDayFilter,
  timelineStatusFilter,
  tournamentStarted,
  visibleTimelineSlots,
}: AdminTournamentTimelinePanelProps) {
  const courtTexts = siteTexts.challengeEngine.courtStatus;
  const editDraftForSlot = (slot: AdminTournamentScheduleSlot) => {
    const startLocal = toDateTimeLocal(slot.slotStartAt);
    const endAt = new Date(slot.slotEndAt);
    const startAt = new Date(slot.slotStartAt);
    const durationMs = endAt.getTime() - startAt.getTime();
    const durationMinutes =
      Number.isFinite(durationMs) && durationMs > 0
        ? Math.round(durationMs / 60000)
        : 60;
    return {
      date: dateOnlyFromLocal(startLocal),
      startTime: timeOnlyFromLocal(startLocal, "08:00"),
      durationMinutes: String(durationMinutes),
      courtName: slotCourtSelection(slot),
    };
  };
  const splitParticipantLabels = (label: string | null | undefined) =>
    String(label || "")
      .split(" / ")
      .map((item) => item.trim())
      .filter(Boolean);
  const resolvePairCheckInLabel = (
    label: string | null | undefined,
    fallback: string,
  ) => splitParticipantLabels(label).join(" / ") || fallback;
  const buildCheckInSides = (
    match: AdminTournamentBracketMatch | AdminTournamentScheduleSlot,
  ): [MatchCheckInSide, MatchCheckInSide] => {
    return [
      {
        id: `timeline-${match.id}-A`,
        label: resolvePairCheckInLabel(match.sideALabel, "Dupla A"),
        matchId: Number(match.id || 0) || null,
        participantCpfs:
          Number(match.sideARegistrationId || 0) > 0 && !match.sideAIsBye
            ? [match.sideAPlayerCpf || "", match.sideAPartnerCpf || ""]
            : [],
      },
      {
        id: `timeline-${match.id}-B`,
        label: resolvePairCheckInLabel(match.sideBLabel, "Dupla B"),
        matchId: Number(match.id || 0) || null,
        participantCpfs:
          Number(match.sideBRegistrationId || 0) > 0 && !match.sideBIsBye
            ? [match.sideBPlayerCpf || "", match.sideBPartnerCpf || ""]
            : [],
      },
    ];
  };

  return (
    <div className="admin-tournament-ops-panel">
      <div className="admin-tournament-timeline-filters">
        <label className="field">
          <span>Categoria</span>
          <select
            value={timelineCategoryFilter}
            onChange={(event) => setTimelineCategoryFilter(event.target.value)}
          >
            <option value="all">Todas</option>
            {categories.map((category) => (
              <option key={category.categoryKey} value={category.categoryKey}>
                {category.categoryName}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Dia</span>
          <select
            value={timelineDayFilter}
            onChange={(event) => setTimelineDayFilter(event.target.value)}
          >
            <option value="all">Todos</option>
            {dayOptions.map((day) => (
              <option key={day} value={day}>
                {day.split("-").reverse().join("/")}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select
            value={timelineStatusFilter}
            onChange={(event) => setTimelineStatusFilter(event.target.value)}
          >
            <option value="all">Todos</option>
            <option value="scheduled">Agendados</option>
            <option value="in_progress">Em quadra</option>
            <option value="finished">Finalizados</option>
          </select>
        </label>
      </div>
      {loadingSchedule ? (
        <Spinner label="Carregando timeline..." />
      ) : !visibleTimelineSlots.length ? (
        <EmptyState
          className="admin-empty-state"
          icon={<span className="material-symbols-outlined">event_busy</span>}
          title="Nenhum jogo agendado na timeline."
        />
      ) : (
        <div className="admin-tournament-timeline-list">
          {visibleTimelineSlots.map((slot) => {
            const edit = scheduleEdits[String(slot.id)] || editDraftForSlot(slot);
            const status = timelineStatusValue(slot);
            const matchId = slotMatchId(slot);
            const matchForSlot: AdminTournamentBracketMatch = {
              id: matchId,
              roundNumber: Number(slot.roundNumber || 0),
              roundCode: String(slot.roundCode || ""),
              matchNumber: Number(slot.matchNumber || 0),
              sideARegistrationId: slot.sideARegistrationId,
              sideBRegistrationId: slot.sideBRegistrationId,
              sideALabel: slot.sideALabel,
              sideBLabel: slot.sideBLabel,
              sideAPlayerCpf: slot.sideAPlayerCpf,
              sideAPartnerCpf: slot.sideAPartnerCpf,
              sideBPlayerCpf: slot.sideBPlayerCpf,
              sideBPartnerCpf: slot.sideBPartnerCpf,
              sideAIsBye: Boolean(slot.sideAIsBye),
              sideBIsBye: Boolean(slot.sideBIsBye),
              winnerSide: slot.winnerSide,
              decidedBy: "match",
              status,
              sets: slot.sets || [],
            };
            const timelineMatch = bracketMatchById.get(matchId) || matchForSlot;
            const resultLabel =
              Array.isArray(timelineMatch.sets) &&
              timelineMatch.sets.length > 0
                ? formatSets(timelineMatch)
                : "";
            const canStartMatch = Boolean(matchId);
            const isOnCourt = status === "in_progress";
            const canEditScore = Boolean(matchId);
            const scoreButtonLabel = resultLabel
              ? "Editar resultado"
              : "Adicionar resultado";
            const scoreButtonTitle = !matchId
              ? "Esta linha ainda não tem partida vinculada."
              : undefined;
            const elapsedLabel = inProgressElapsedLabel({
              status,
              actualStartedAt: slot.actualStartedAt,
              nowMs,
            });
            const checkInSides = buildCheckInSides(timelineMatch);
            const startButtonDisabled = saving || !canStartMatch;
            const startButtonTitle = undefined;
            return (
              <article
                key={slot.id}
                className={`admin-tournament-timeline-card ${timelineStatusClass(
                  status,
                )}`}
              >
                <div className="admin-tournament-timeline-main">
                  <time>{formatDateTimeLabel(slot.slotStartAt)}</time>
                  <strong className="admin-tournament-timeline-phase">
                    {categoryLabel(categories, slot.categoryKey)} ·{" "}
                    {roundLabel(slot.roundCode, slot.roundNumber)}
                  </strong>
                  <div className="admin-tournament-timeline-matchup">
                    <span>{slot.sideALabel || "A definir"}</span>
                    <strong>x</strong>
                    <span>{slot.sideBLabel || "A definir"}</span>
                  </div>
                  <span className="admin-tournament-status-pill">
                    {matchStatusLabel(status)}
                  </span>
                  {resultLabel && (
                    <div className="admin-tournament-timeline-score">
                      <span>Resultado</span>
                      <strong>{resultLabel}</strong>
                    </div>
                  )}
                  {elapsedLabel && (
                    <small className="admin-tournament-elapsed">
                      {elapsedLabel}
                    </small>
                  )}
                  <AdminMatchCheckInControl
                    canEdit
                    checkins={participantCheckins}
                    onToggleSide={onToggleCheckInSide}
                    pendingSideId={pendingCheckInSideId}
                    sides={checkInSides}
                  />
                </div>
                <div className="admin-tournament-timeline-edit">
                  <div className="admin-tournament-timeline-timebox">
                    <span>{siteTexts.admin.misc.scheduledTime}</span>
                    <strong>{formatDateTimeLabel(slot.slotStartAt)}</strong>
                    {slot.actualStartedAt && (
                      <small>
                        {siteTexts.admin.misc.startedPrefix} {formatDateTimeLabel(slot.actualStartedAt)}
                      </small>
                    )}
                    {slot.actualFinishedAt && (
                      <small>
                        {siteTexts.admin.misc.finishedPrefix}{" "}
                        {formatDateTimeLabel(slot.actualFinishedAt)}
                      </small>
                    )}
                  </div>
                  <OrganizerDatePicker
                    label="Data"
                    value={edit.date}
                    onChange={(value) =>
                      setScheduleEdits((current) => ({
                        ...current,
                        [String(slot.id)]: {
                          ...edit,
                          date: value,
                        },
                      }))
                    }
                  />
                  <OrganizerTimeInput
                    label="Horário"
                    value={edit.startTime}
                    onChange={(value) =>
                      setScheduleEdits((current) => ({
                        ...current,
                        [String(slot.id)]: {
                          ...edit,
                          startTime: value,
                        },
                      }))
                    }
                  />
                  <label className="field">
                    <span>Duração</span>
                    <input
                      type="number"
                      min={5}
                      value={edit.durationMinutes}
                      onChange={(event) =>
                        setScheduleEdits((current) => ({
                          ...current,
                          [String(slot.id)]: {
                            ...edit,
                            durationMinutes: event.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Quadra</span>
                    <select
                      value={edit.courtName}
                      onChange={(event) =>
                        setScheduleEdits((current) => ({
                          ...current,
                          [String(slot.id)]: {
                            ...edit,
                            courtName: event.target.value,
                          },
                        }))
                      }
                    >
                      <option value="">A definir</option>
                      {courtNames.map((court) => (
                        <option key={court} value={court}>
                          {court}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="admin-tournament-match-actions">
                  <Button
                    type="button"
                    variant="pill"
                    disabled={saving || !matchId}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onSaveSlotSchedule(slot, edit);
                    }}
                  >
                    Salvar horário
                  </Button>
                  <Button
                    type="button"
                    variant="pill"
                    disabled={startButtonDisabled}
                    title={startButtonTitle}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onStartSlotMatch(slot, matchForSlot, edit.courtName);
                    }}
                  >
                    {isOnCourt ? courtTexts.removeFromCourt : courtTexts.putOnCourt}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={saving || !canEditScore}
                    title={scoreButtonTitle}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onOpenScoreModal(
                        timelineMatch,
                        String(slot.categoryKey || categoryKey),
                      );
                    }}
                  >
                    {scoreButtonLabel}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
